import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
// Interfaces and Types
import { SalesAssociateWashoutContentProps } from "@/Washouts/types/sales";
// API
import { getCommissionCyclesCitCommissionPayout, getSystemSettingsCitCommissionPayout } from "@/Washouts/api/common/queries";
// Components
import EmployeeMetaSection from "@/Washouts/components/EmployeeMetaSection";

// Tables
import SalesPerformanceTable from "@/Washouts/components/tables/Sales/Associates/SalesPerformanceTable";
import PendingDealsTable from "@/Washouts/components/tables/Sales/Associates/PendingDealsTable";
import ChargebacksTable from "@/Washouts/components/tables/Sales/Associates/ChargebacksTable";
import BirdDogsTable from "@/Washouts/components/tables/Sales/Associates/BirdDogsTable";
import SpiffsTable from "@/Washouts/components/tables/Sales/Associates/SpiffsTable";
import AutoAlertsTable from "@/Washouts/components/tables/Sales/Associates/AutoAlertsTable";
import Compensation from "@/Washouts/components/tables/Common/CompensationTable";
import CallActivityPerformanceWrapper, { getCallActivityData } from "@/Washouts/components/CallActivityPerformanceWrapper";
// MUI
import { Grid } from "@mui/material";
// Constants
import { BASE_TABLE_PROPS, DEFAULT_TABLE_MAX_ROWS } from "@/Washouts/constants";
import { CompensationTableProps } from "@/Washouts/components/tables/Common/CompensationTable/types";
import { IWashoutModel } from "@/Washouts/types/commons";
import { useIsWashoutEditable } from "@/Washouts/hooks/WashoutReview/useIsWashoutEditable";
import { CardsDenseGrid } from "@/Washouts/layouts/CardsGrid";
import { resolveCardsData } from "@/Washouts/helpers/CardsGrid";
import { CustomerSatisfactionIndexInput, DeductionItem, LongevityInput, SalesPerformanceInput } from "@/Washouts/types/CardsGrid";

function isReady(model: SalesAssociateWashoutContentProps["model"] = undefined, isLoading = false) {
	return Boolean(model) || isLoading;
}

function CarryOverCommissionsSection({
	model,
	stillLoading,
	citCommissionPayoutInitialPositions,
	systemSettingsCitCommissionPayout,
}: Readonly<
	SalesAssociateWashoutContentProps & {
		stillLoading: boolean;
		citCommissionPayoutInitialPositions: string[];
		systemSettingsCitCommissionPayout: string[];
	}
>) {
	if (
		Boolean(citCommissionPayoutInitialPositions?.some((position: string) => position === model?.employee?.position)) ||
		Boolean(systemSettingsCitCommissionPayout?.some((position: string) => position === model?.employee?.position))
	) {
		return (
			<Grid item>
				<SalesPerformanceTable
					title={"Carry Over Commissions"}
					emptyTitle={"Carry Over Commissions"}
					data={model?.carryOver?.sales}
					divider
					dataPresent={Boolean(model?.carryOver?.sales)}
					loading={stillLoading}
					aggregationToUse={"selectedSum"}
					totalsTypeTitle={model?.carryOver?.totals?.totalsTypeTitle}
					tableProps={{ ...BASE_TABLE_PROPS }}
				/>
			</Grid>
		);
	}
	return null;
}

function ChargebacksSection({ model, stillLoading }: Readonly<SalesAssociateWashoutContentProps & { stillLoading: boolean }>) {
	const preOwnLength = model?.preOwnedChargebacks?.chargebacks?.length ?? 0;
	const newCarLength = model?.newChargebacks?.chargebacks?.length ?? 0;
	const maxRows = Math.min(DEFAULT_TABLE_MAX_ROWS, Math.max(preOwnLength, newCarLength));
	const tableProps = {
		...BASE_TABLE_PROPS,
		maxRows: maxRows + 1,
		enableRowsFiller: preOwnLength > 0 || newCarLength > 0,
	};
	return (
		<Grid container className="px-[30px] py-[15px] flex gap-x-[48px]">
			<Grid item className="flex-grow max-w-[644px]">
				<ChargebacksTable
					key={`${model?._id}-newChargebacks`}
					title="New Vehicle Chargebacks"
					data={model?.newChargebacks?.chargebacks ?? []}
					dataPresent={Boolean(model)}
					loading={stillLoading}
					tableProps={{ ...tableProps }}
				/>
			</Grid>

			<Grid item className="flex-grow max-w-[644px]">
				<ChargebacksTable
					key={`${model?._id}-preownedChargebacks`}
					title="Pre-Owned Vehicle Chargebacks"
					data={model?.preOwnedChargebacks?.chargebacks ?? []}
					dataPresent={Boolean(model)}
					loading={stillLoading}
					tableProps={{ ...tableProps }}
				/>
			</Grid>
		</Grid>
	);
}

function SpiffsAndAlertsSection({ model, stillLoading }: Readonly<SalesAssociateWashoutContentProps & { stillLoading: boolean }>) {
	const alertsLength = model?.autoAlerts?.autoAlerts?.length ?? 0;
	const spiffsLength = model?.spiffs?.spiffs?.length ?? 0;
	const maxRows = Math.min(DEFAULT_TABLE_MAX_ROWS, Math.max(spiffsLength, alertsLength));
	const tableProps = {
		...BASE_TABLE_PROPS,
		maxRows: maxRows + 1,
		enableRowsFiller: spiffsLength > 0 || alertsLength > 0,
	};
	return (
		<Grid container className="px-[30px] py-[15px] flex gap-x-[48px]">
			<Grid item className="flex-grow max-w-[644px]">
				<SpiffsTable
					key={`${model?._id}-spiffs`}
					data={model?.spiffs?.spiffs ?? []}
					dataPresent={Boolean(model)}
					loading={stillLoading}
					tableProps={{ ...tableProps }}
				/>
			</Grid>

			<Grid item className="flex-grow max-w-[644px]">
				<AutoAlertsTable
					key={`${model?._id}-alerts`}
					title="Opportunity Mining"
					data={model?.autoAlerts?.autoAlerts ?? []}
					dataPresent={Boolean(model)}
					loading={stillLoading}
					tableProps={{ ...tableProps }}
				/>
			</Grid>
		</Grid>
	);
}

function BirdsAndCallActivitySection({
	model,
	stillLoading,
	...props
}: Readonly<SalesAssociateWashoutContentProps & { stillLoading: boolean }>) {
	const birdDogsLength = model?.birdDogs?.birdDogs?.length ?? 0;
	const callActivityLength = getCallActivityData(model as IWashoutModel).length;
	const maxRows = Math.min(DEFAULT_TABLE_MAX_ROWS, Math.max(callActivityLength, birdDogsLength)) + 1;
	const enableRowsFiller = callActivityLength > 0 || birdDogsLength > 0;
	return (
		<Grid item container className="px-[30px] pt-[20px] pb-[48px] flex gap-x-[48px]">
			<Grid item className="flex-grow max-w-[644px]">
				<Grid>
					<BirdDogsTable
						key={`${model?._id}-birdDogs`}
						data={model?.birdDogs?.birdDogs ?? []}
						dataPresent={Boolean(model)}
						loading={stillLoading}
						tableProps={{ ...BASE_TABLE_PROPS, maxRows, enableRowsFiller }}
					/>
				</Grid>
			</Grid>

			<Grid item className="flex-grow max-w-[644px]">
				<CallActivityPerformanceWrapper
					key={`${model?._id}-callActivity`}
					model={model as IWashoutModel}
					editable={props.selected?.status === "ACTIVE" || props.selected?.status === "CLOSING"}
					dataPresent
					loading={stillLoading}
					onActionUpdate={() => {}}
					tableProps={{ ...BASE_TABLE_PROPS, maxRows, enableRowsFiller }}
				/>
			</Grid>
		</Grid>
	);
}

function CompensationSection({ model, stillLoading, ...props }: Readonly<SalesAssociateWashoutContentProps & { stillLoading: boolean }>) {
	const isEditable = useIsWashoutEditable({
		cycleStatus: props?.selected?.status ?? "",
		positionStatus: model?.review?.status ?? "",
		hasRole: Boolean(props.overrideCompensation),
	});

	return (
		<Grid item container className="px-[30px] pt-[20px] pb-[48px] flex gap-x-[48px]">
			<Grid item className="flex-grow max-w-[644px]">
				<Grid></Grid>
			</Grid>

			<Grid item className="flex-grow max-w-[644px]">
				<Compensation
					key={`${model?._id}-compensation`}
					data={model?.summary?.summary ?? []}
					addedData={model?.addedCompensations ?? []}
					tooltip
					editable={isEditable}
					columns={[
						{
							field: "tier",
							headerName: "Tier",
							align: "left",
							headerAlign: "left",
						},
					]}
					dataPresent={Boolean(model)}
					loading={stillLoading}
					onActionUpdate={props.handleSummaryChange as CompensationTableProps["onActionUpdate"]}
				/>
			</Grid>
		</Grid>
	);
}

export default function SalesAssociateWashoutContent(props: Readonly<SalesAssociateWashoutContentProps>) {
	const { model, selected, hideEmployeeMeta = false, isLoading = false, disableBoxShadow = false } = props;

	const { data: citCommissionPayoutInitialPositions, isLoading: isLoadingCommissionPayout } = useQuery({
		queryKey: ["citCommissionPayout", props.locationId, props.model?.commissionCycleId],
		queryFn: async () => {
			const commissionCycle = await getCommissionCyclesCitCommissionPayout(props.locationId, props.model?.commissionCycleId);
			return commissionCycle?.citCommissionPayoutInitialPositions ?? [];
		},

		enabled: !!props.locationId && !!props.model?.commissionCycleId,
		staleTime: Infinity,
	});

	const { data: systemSettingsCitCommissionPayout, isLoading: isLoadingSettingsCitCommission } = useQuery({
		queryKey: ["systemSettingsCitCommissionPayout", "sales", props.locationId],
		queryFn: async () => {
			const systemSettings = await getSystemSettingsCitCommissionPayout(props.locationId);
			return systemSettings?.citCommissionPayout?.toPositions ?? [];
		},

		enabled: !!props.locationId,
		staleTime: Infinity,
	});

	const stillLoading = isLoading || isLoadingCommissionPayout || isLoadingSettingsCitCommission;
	const hasReviews = Boolean(model?.customerSatisfactionIndex?.isPresent);

	if (isReady(model, isLoading)) {
		return (
			<Grid container direction="column" className={clsx({ "shadow-white-section": !disableBoxShadow })} rowSpacing={0}>
				<EmployeeMetaSection
					{...props.employeeMetaProps}
					model={model}
					hidden={hideEmployeeMeta}
					loading={stillLoading}
					secondaryColumns={["DAYS_OUT", "RANKING"]}
				/>

				<Grid item>
					<SalesPerformanceTable
						divider
						data={model?.salesPerformance?.sales}
						columnVisibility={model?.salesPerformance?.columnVisibility}
						dataPresent={Boolean(model)}
						loading={stillLoading}
						tableProps={{ ...BASE_TABLE_PROPS, maxRows: 16 }}
					/>
				</Grid>

				<Grid item>
					<PendingDealsTable
						divider
						data={model?.salesLog?.sales}
						dataPresent={Boolean(model)}
						loading={stillLoading}
						tableProps={{ ...BASE_TABLE_PROPS }}
					/>
				</Grid>

				<CarryOverCommissionsSection
					model={model}
					stillLoading={stillLoading}
					citCommissionPayoutInitialPositions={citCommissionPayoutInitialPositions}
					systemSettingsCitCommissionPayout={systemSettingsCitCommissionPayout}
				/>

				<CardsDenseGrid
					loading={stillLoading}
					data={resolveCardsData({
						longevity: model?.longevity as LongevityInput,
						...(hasReviews && {
							customerSatisfactionIndex: model?.customerSatisfactionIndex as CustomerSatisfactionIndexInput,
						}),
						salesPerformance: model?.salesPerformance as unknown as SalesPerformanceInput,
						deductions: model?.deductions?.deductions as unknown as DeductionItem[],
					})}
				/>

				<ChargebacksSection model={model} stillLoading={stillLoading} />

				<SpiffsAndAlertsSection {...props} stillLoading={stillLoading} />

				<BirdsAndCallActivitySection {...props} stillLoading={stillLoading} />

				<CompensationSection {...props} selected={selected} stillLoading={stillLoading} />
			</Grid>
		);
	}
	return null;
}
